import {
  Box,
  Button,
  Typography,
  useTheme,
  TextField,
  MenuItem,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { EventViewModel } from "../../models/eventViewModel";
import { RecordingViewModel } from "../../models/recordingViewModel";
import { UpdateEventFeedbackInput } from "../../../../common/types/events/api";
import useLocationFromCache from "../../../../common/hooks/useLocationFromCache";
import FeedbackThumbsTableCellContainer from "../feedback/FeedbackThumbsTableCellContainer";
import FeedbackDetailsSelect from "../feedback/FeedbackDetailsSelect";
import { FeedbackType } from "../../../../API";
import { useUpdateEvent } from "../../hooks/events/useUpdateEvent";
import {
  DEFAULT_EMISSION_TYPE,
  EMISSION_TYPES,
  EQUIPMENT_CLASSIFICATIONS,
  SOURCES,
} from "../../../data-hub/constants";

interface EventInformationTabProps {
  event: EventViewModel;
  newestRecording?: RecordingViewModel;
  onUpdateEventFeedback: (
    input: UpdateEventFeedbackInput,
    event: EventViewModel
  ) => Promise<void>;
  onDirtyChange?: (dirty: boolean) => void;
}

const EventInformationTab = ({
  event,
  newestRecording,
  onUpdateEventFeedback,
  onDirtyChange,
}: EventInformationTabProps): JSX.Element => {
  const theme = useTheme();
  const { getCachedLocationName } = useLocationFromCache();
  const { updateEvent, loading: saving } = useUpdateEvent();

  const zoneId = newestRecording?.rawEventInfo?.data?.zone_id ?? "N/A";
  const instanceName =
    newestRecording?.rawEventInfo?.data?.instance_name ?? "N/A";
  const isLeakEvent = event.modelId?.toLowerCase() === "gasleak";
  const locationName = getCachedLocationName(event.location) || "N/A";

  const initialValues = useMemo(
    () => ({
      equipmentClassification:
        !event?.equipmentClassification ||
        event?.equipmentClassification === "unknown"
          ? ""
          : event?.equipmentClassification,
      source:
        !event?.eventSource || event?.eventSource === "unknown"
          ? ""
          : event?.eventSource,
      emissionType: event?.emissionType || DEFAULT_EMISSION_TYPE,
      summary: event?.summary ?? "",
    }),
    [
      event?.equipmentClassification,
      event?.emissionType,
      event?.eventSource,
      event?.summary,
    ]
  );

  const [formValues, setFormValues] = useState(initialValues);
  const [displayValues, setDisplayValues] = useState(initialValues);
  const [detailsErrors, setDetailsErrors] = useState<{
    emissionType?: string;
  }>({});
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [isEditingSummary, setIsEditingSummary] = useState(false);

  // Inform parent about dirty state (unsaved edits)
  useEffect(() => {
    const isDetailsDirty =
      isLeakEvent &&
      (isEditingDetails ||
        formValues.equipmentClassification !==
          displayValues.equipmentClassification ||
        formValues.source !== displayValues.source ||
        formValues.emissionType !== displayValues.emissionType);
    const isSummaryDirty =
      isEditingSummary || formValues.summary !== displayValues.summary;

    const dirty = isDetailsDirty || isSummaryDirty;

    onDirtyChange?.(dirty);
  }, [
    isLeakEvent,
    isEditingDetails,
    isEditingSummary,
    formValues.equipmentClassification,
    formValues.source,
    formValues.emissionType,
    formValues.summary,
    displayValues.equipmentClassification,
    displayValues.source,
    displayValues.emissionType,
    displayValues.summary,
    onDirtyChange,
  ]);

  const handleEditDetailsClick = () => {
    setIsEditingSummary(false);

    setFormValues(displayValues);

    setDetailsErrors({});

    setIsEditingDetails(true);
  };

  const handleCancelDetails = () => {
    setFormValues(displayValues);

    setDetailsErrors({});

    setIsEditingDetails(false);
  };

  const handleSaveDetails = async () => {
    if (isLeakEvent && !formValues.emissionType) {
      setDetailsErrors({ emissionType: "Emission Type is required" });

      return;
    }

    try {
      await updateEvent({
        customerId: event.customerId,
        eventId: event.id,
        equipmentClassification: formValues.equipmentClassification ?? "",
        eventSource: formValues.source ?? "",
        emissionType: formValues.emissionType ?? "",
      });

      setDisplayValues(v => ({
        ...v,
        equipmentClassification: formValues.equipmentClassification ?? "",
        source: formValues.source ?? "",
        emissionType: formValues.emissionType ?? "",
      }));

      setIsEditingDetails(false);
    } catch {
      // handled by hook
    }
  };

  const handleEditSummaryClick = () => {
    setIsEditingDetails(false);

    setFormValues(displayValues);

    setIsEditingSummary(true);
  };

  const handleCancelSummary = () => {
    setFormValues(displayValues);

    setIsEditingSummary(false);
  };

  const handleSaveSummary = async () => {
    try {
      await updateEvent({
        customerId: event.customerId,
        eventId: event.id,
        summary: formValues.summary ?? "",
      });

      setDisplayValues(v => ({
        ...v,
        summary: formValues.summary ?? "",
      }));

      setIsEditingSummary(false);
    } catch {
      // handled by hook
    }
  };

  const labelStyle = {
    minWidth: "160px",
  };

  const sectionTitleStyle = {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 500,
    color: `${theme.palette.text.secondary} !important`,
  };

  const sectionLabelStyle = {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 500,
    color: `${theme.palette.text.secondary} !important`,
    minWidth: "160px",
  };

  const editButtonStyle = {
    borderRadius: "9999px",
    px: 2,
    textTransform: "none",
    borderColor: theme.palette.grey[400],
    color: theme.palette.text.secondary,
    "&:hover": {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
  };

  return (
    <Box>
      {/* Metadata Section */}
      <Box display="flex" flexDirection="column" gap={2} mb={4}>
        <Typography sx={sectionTitleStyle}>Metadata</Typography>
        <Box display="flex">
          <Typography variant="body2" color="text.secondary" sx={labelStyle}>
            Location
          </Typography>
          <Typography variant="body2">{locationName}</Typography>
        </Box>
        <Box display="flex">
          <Typography variant="body2" color="text.secondary" sx={labelStyle}>
            Device ID
          </Typography>
          <Typography variant="body2">{event.camera_id || "N/A"}</Typography>
        </Box>

        {isLeakEvent && (
          <>
            <Box display="flex">
              <Typography
                variant="body2"
                color="text.secondary"
                sx={labelStyle}
              >
                Zone ID
              </Typography>
              <Typography variant="body2">{zoneId}</Typography>
            </Box>
            <Box display="flex">
              <Typography
                variant="body2"
                color="text.secondary"
                sx={labelStyle}
              >
                Instance Name
              </Typography>
              <Typography variant="body2">{instanceName}</Typography>
            </Box>
          </>
        )}
      </Box>

      {/* Feedback Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
        mb={3}
      >
        <Box display="flex" alignItems="center" mb={3}>
          <Typography sx={sectionLabelStyle}>Feedback</Typography>
          <FeedbackThumbsTableCellContainer
            event={event}
            onUpdateEventFeedback={onUpdateEventFeedback}
          />
        </Box>
        {event.feedback && (
          <Box display="flex" alignItems="center" mb={3}>
            <FeedbackDetailsSelect
              feedbackType={event.feedback as FeedbackType}
              selectedValues={event.mitigatedRisk ?? []}
              onSelectionChange={async (values: string[]) => {
                const input: UpdateEventFeedbackInput = {
                  pk: event.customerId,
                  sk: event.id,
                  feedback: event.feedback?.toString() ?? null,
                  mitigatedRisk: values.length > 0 ? values.join(";") : null,
                };

                await onUpdateEventFeedback(input, event);
              }}
            />
          </Box>
        )}
      </Box>

      {/* Details Section */}
      <Box display="flex" flexDirection="column" gap={2} mb={4}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={sectionTitleStyle}>
            {isLeakEvent ? "Details" : "Summary"}
          </Typography>
          {isLeakEvent ? (
            !isEditingDetails ? (
              <Button
                variant="outlined"
                size="small"
                sx={editButtonStyle}
                onClick={handleEditDetailsClick}
              >
                Edit
              </Button>
            ) : (
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  size="small"
                  sx={editButtonStyle}
                  onClick={handleCancelDetails}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSaveDetails}
                  disabled={saving}
                >
                  Save
                </Button>
              </Box>
            )
          ) : !isEditingSummary ? (
            <Button
              variant="outlined"
              size="small"
              sx={editButtonStyle}
              onClick={handleEditSummaryClick}
            >
              Edit
            </Button>
          ) : (
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                size="small"
                sx={editButtonStyle}
                onClick={handleCancelSummary}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleSaveSummary}
                disabled={saving}
              >
                Save
              </Button>
            </Box>
          )}
        </Box>
        {!isEditingDetails && isLeakEvent && (
          <>
            <Box display="flex">
              <Typography
                variant="body2"
                color="text.secondary"
                sx={labelStyle}
              >
                Source
              </Typography>
              <Typography variant="body2">
                {displayValues.source || "N/A"}
              </Typography>
            </Box>
            <Box display="flex">
              <Typography
                variant="body2"
                color="text.secondary"
                sx={labelStyle}
              >
                Equipment
              </Typography>
              <Typography variant="body2">
                {displayValues.equipmentClassification || "N/A"}
              </Typography>
            </Box>
            <Box display="flex">
              <Typography
                variant="body2"
                color="text.secondary"
                sx={labelStyle}
              >
                Emission Type
              </Typography>
              <Typography variant="body2">
                {displayValues.emissionType || "N/A"}
              </Typography>
            </Box>
          </>
        )}
        {!isEditingSummary && !isLeakEvent && (
          <Typography variant="body2" color="text.secondary">
            {displayValues.summary || "No summary available."}
          </Typography>
        )}
        {isEditingDetails && isLeakEvent && (
          <>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={labelStyle}
              >
                Source
              </Typography>
              <TextField
                select
                size="small"
                value={formValues.source}
                onChange={e =>
                  setFormValues(v => ({ ...v, source: e.target.value }))
                }
                sx={{ minWidth: 240 }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {SOURCES.map(opt => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={labelStyle}
              >
                Equipment
              </Typography>
              <TextField
                select
                size="small"
                value={formValues.equipmentClassification}
                onChange={e =>
                  setFormValues(v => ({
                    ...v,
                    equipmentClassification: e.target.value,
                  }))
                }
                sx={{ minWidth: 240 }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {EQUIPMENT_CLASSIFICATIONS.map(opt => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={labelStyle}
              >
                Emission Type
              </Typography>
              <TextField
                select
                required
                size="small"
                value={formValues.emissionType}
                onChange={e => {
                  setFormValues(v => ({ ...v, emissionType: e.target.value }));

                  if (detailsErrors.emissionType) setDetailsErrors({});
                }}
                error={!!detailsErrors.emissionType}
                helperText={detailsErrors.emissionType}
                sx={{ minWidth: 240 }}
              >
                {EMISSION_TYPES.map(opt => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </>
        )}
        {isEditingSummary && !isLeakEvent && (
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={3}
            value={formValues.summary}
            onChange={e =>
              setFormValues(v => ({ ...v, summary: e.target.value }))
            }
            placeholder="Add a summary (optional)"
          />
        )}
      </Box>

      {/* Summary Section */}
      {isLeakEvent && (
        <Box display="flex" flexDirection="column" gap={2} mb={4}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography sx={sectionTitleStyle}>Summary</Typography>
            {!isEditingSummary ? (
              <Button
                variant="outlined"
                size="small"
                sx={editButtonStyle}
                onClick={handleEditSummaryClick}
              >
                Edit
              </Button>
            ) : (
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  size="small"
                  sx={editButtonStyle}
                  onClick={handleCancelSummary}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSaveSummary}
                  disabled={saving}
                >
                  Save
                </Button>
              </Box>
            )}
          </Box>
          {!isEditingSummary && (
            <Typography variant="body2" color="text.secondary">
              {displayValues.summary || "No summary available."}
            </Typography>
          )}
          {isEditingSummary && (
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={3}
              value={formValues.summary}
              onChange={e =>
                setFormValues(v => ({ ...v, summary: e.target.value }))
              }
              placeholder="Add a summary (optional)"
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default EventInformationTab;
